<template>
	<div
		:class="[
			'fight-result',
			{
				'fight-result--defeat':
					ssr.siegeResult === SiegeSystemResult.DEFEAT,
			},
		]"
	>
		<div class="fight-result__header">
			<div class="fight-result__header__text">{{ headerText }}</div>
		</div>
		<div class="fight-result__subheader">{{ subheaderText }}</div>
		<HorizontalDivider />
		<div class="fight-result__content">
			<div class="fight-result__content__header">
				{{ translate("ui.battle_report") }}
			</div>
			<div
				class="fight-result__content__line"
				v-for="report in reports"
				:key="report.text"
			>
				<div class="fight-result__content__line__text">
					{{ report.text }}
				</div>
				<div class="after-dots">
					<div class="fight-result__content__line__value">
						{{ report.value }}
					</div>
				</div>
			</div>
		</div>
		<div class="fight-result__detail" v-if="rewards">
			<div class="fight-result__detail__content">
				<BidirectionalTextLabel
					:text="translate('ui.rewards')"
					:theme="LabelThemes.LIGHT_GREEN"
					class="rewards-label"
				/>
				<div
					v-for="(amount, currency, index) in rewards"
					:key="index"
					class="fight-result__detail-reward"
				>
					<template v-if="amount && amount > 0">
						<Tooltip :key="currency" :placement="Placements.TOP">
							<template #element>
								<AppPrice
									:key="index"
									:amount="amount || 0"
									:currency="currency"
									:resourceCheck="false"
									:iconSize="16"
								/>
							</template>
							<template #tooltip>
								<span>
									{{
										translate(
											`resource.${resourceTypeToCurrencyType(
												currency,
											)}`,
										)
									}}
								</span>
							</template>
						</Tooltip></template
					>
				</div>
			</div>
			<RectangleBoxContainer />
		</div>
		<div class="fight-result__message" v-else>
			<HorizontalDecorDivider with-sides />
			<div>
				{{ translate("ui.defeat_info") }}
			</div>
		</div>

		<AppTextButton
			class="fight-result__popup-btn"
			:type="buttonType"
			:text="buttonText"
			@click="claimRewards"
		/>
	</div>
</template>

<script lang="ts" setup>
import AppPrice from "@/components/AppPrice.vue";
import AppTextButton from "@/components/AppTextButton.vue";
import BidirectionalTextLabel from "@/components/BidirectionalTextLabel.vue";
import HorizontalDivider from "@/components/HorizontalDivider.vue";
import RectangleBoxContainer from "@/components/SVG/RectangleBoxContainer.vue";
import Tooltip from "@/components/Tooltip.vue";
import type { ResourceType } from "@/game/entities/config/resources.ts";
import { ToastPlacement } from "@/game/ToastMessage.ts";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter.ts";
import useSiegeSystemRepository from "@/hooks/useSiegeSystemRepository.ts";
import useSocketService, { EmitStores } from "@/hooks/useSocketService.ts";
import { usePopupStore } from "@/store/usePopupStore";
import useResourcesStore from "@/store/useResourcesStore.ts";
import useSiegeSystemStore, {
	SiegeSystemResult,
} from "@/store/useSiegeSystemStore";
import { type Reward } from "@/types/tile.ts";
import { LabelThemes, Placements, StatusTypes } from "@/types/ui.ts";
import { resourceTypeToCurrencyType } from "@/utils/formatPrice.ts";
import { formatTimeString } from "@/utils/time.ts";
import { translate } from "@/utils/translate.ts";
import { computed, markRaw, ref } from "vue";
import HorizontalDecorDivider from "../SVG/HorizontalDecorDivider.vue";

const popupStore = markRaw(usePopupStore());

const emitter = markRaw(useEmitter());
const socketService = markRaw(useSocketService());
const ssr = markRaw(useSiegeSystemRepository());

const resourcesStore = useResourcesStore();
const sss = useSiegeSystemStore();

const headerText = computed<string>((): string => {
	if (ssr.siegeResult === SiegeSystemResult.DEFEAT)
		return translate("ui.defeat");
	return translate("ui.victory");
});
const subheaderText = computed<string>((): string => {
	if (ssr.siegeResult === SiegeSystemResult.DEFEAT)
		return translate("ui.our_units_defeated");
	return translate("ui.territory_liberated");
});
const isPending = ref<boolean>(false);

const reports = computed<
	Array<{ text: string; value: number | string | null }>
>(
	(): Array<{ text: string; value: number | string | null }> => [
		{
			text: translate("ui.battle_duration"),
			value: formatTimeString(ssr.duration || 0),
		},
		{ text: translate("ui.damage_dealt"), value: ssr.damageDealt },
		{ text: translate("ui.damage_taken"), value: ssr.damageTaken },
		{
			text: translate("ui.units_defeated"),
			value: `${ssr.enemiesDefeated}/${ssr.enemiesTotal}`,
		},
	],
);

const rewards = computed<Reward | null>((): Reward | null => ssr.rewards);

const claimRewards = async (): Promise<void> => {
	const { tileIndex, range } = sss.currentBattlefield;
	if (!tileIndex || !range) return;

	ssr.clearBattlefield(ssr.siegeResult, tileIndex, range);
	ssr.rerenderBattlefieldLabelByTileIndex(tileIndex);

	if (!rewards.value) {
		ssr.resetSiege();
		popupStore.hidePopup();
		await socketService.emitSaveRequest({
			stores: [EmitStores.SIEGE_SYSTEM],
		});
		return;
	}
	if (isPending.value) return;
	isPending.value = true;

	try {
		const currentRewards = { ...rewards.value };
		const entries = Object.entries(currentRewards) as Array<
			[ResourceType, number]
		>;
		entries.forEach(([resource, value]: [ResourceType, number]): void => {
			resourcesStore.increase(resource, value, "siege rewards claim");
		});

		setTimeout((): void => {
			emitter.emit(EmitterEvents.PhaserTileClick, {
				x: 0,
				y: 0,
				reward: currentRewards,
				placement: ToastPlacement.TOP_CENTER,
			});
		}, 500);

		ssr.resetSiege();

		await socketService.emitSaveRequest({
			stores: [EmitStores.RESOURCES, EmitStores.SIEGE_SYSTEM],
		});

		popupStore.hidePopup();
	} catch (error) {
		console.error("Failed to claim siege rewards", error);
	} finally {
		isPending.value = false;
	}
};

const buttonText = computed<string>((): string => {
	return ssr.siegeResult === SiegeSystemResult.DEFEAT
		? translate("button.continue")
		: translate("button.claim");
});

const buttonType = computed<StatusTypes>((): StatusTypes => {
	return ssr.siegeResult === SiegeSystemResult.DEFEAT
		? StatusTypes.DEFAULT_ACTIVE
		: StatusTypes.SUCCESS;
});
</script>

<style lang="scss" scoped>
.fight-result {
	gap: 0.5rem;
	border-width: 0.125rem;
	border-style: solid;
	border-image-source: linear-gradient(to top, #bd9032, #f0d386);
	border-image-slice: 1;
	width: 100%;
	height: 100%;
	@include flex-col-start;

	&__header {
		background-image: url($path-images + "backgrounds/fight-system/img-victory.avif");
		background-size: cover;
		width: 100%;
		height: 10.975rem;
		@include flex-col-end;

		&__text {
			border-width: 0.075rem;
			border-style: solid;
			border-image-source: linear-gradient(
				to right,
				rgb(244, 210, 120, 0),
				rgba(244, 210, 120, 0.8),
				rgba(244, 210, 120, 0.8),
				rgb(244, 210, 120, 0)
			);
			border-image-slice: 1;
			background-image: linear-gradient(
				to right,
				rgb(205, 142, 33, 0),
				rgba(205, 142, 33, 0.78),
				rgba(205, 142, 33, 0.8),
				rgb(205, 142, 33, 0)
			);
			width: 100%;
			height: 2.125rem;
			line-height: 1;
			@include flex-row-center;
			padding-bottom: 0.25rem;
			color: rgb(255, 243, 221);
			font-weight: 800;
			font-size: 1.5rem;
			text-shadow: 0.0437rem 0.0437rem 0.0625rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
		}
	}

	&__subheader {
		width: 100%;
		color: rgb(244, 210, 120);
		font-weight: 900;
		font-size: 1.25rem;
		line-height: 1;
		text-shadow: 0.0437rem 0.0437rem 0.0625rem rgba(0, 0, 0, 0.6);
		text-transform: uppercase;
	}

	.horizontal-divider {
		width: 98%;
	}
	&__content {
		padding: 1.25rem 1.25rem 0.75rem;
		@include flex-col-center;
		gap: 1rem;
		width: 100%;
		line-height: 1;
		&__header {
			color: rgb(255, 243, 221);
			font-weight: 800;
			font-size: 1rem;
			text-shadow: 0.0437rem 0.0437rem 0.0625rem rgba(0, 0, 0, 0.6);
		}

		&__line {
			width: 100%;
			color: rgb(242, 220, 181);
			font-size: 0.9063rem;
			text-shadow: 0.0437rem 0.0437rem 0.0625rem rgba(0, 0, 0, 0.6);
			@include flex-row-between;
			&__text {
			}
			&__value {
				color: #f4d278;
			}

			.after-dots {
				display: flex;
				flex: 1;
				&::before {
					flex: 1;
					margin: 0.25rem 0.25rem 0;
					border-bottom: 0.125rem dotted rgb(242, 220, 181);
					content: "";
				}
			}
		}
	}

	&--defeat {
		border-image-source: linear-gradient(to top, #cf0032, #ff3d3d);

		.fight-result__header {
			background-image: url($path-images + "backgrounds/fight-system/img-defeat.avif");
		}

		.fight-result__header__text {
			border-image-source: linear-gradient(
				to right,
				rgb(255, 61, 61, 0),
				rgba(255, 61, 61, 0.8),
				rgba(255, 61, 61, 0.8),
				rgb(255, 61, 61, 0)
			);
			background-image: linear-gradient(
				to right,
				rgb(207, 0, 50, 0),
				rgba(207, 0, 50, 0.78),
				rgba(207, 0, 50, 0.8),
				rgb(207, 0, 50, 0)
			);
		}
	}

	&__popup-btn {
		margin: 1.25rem 0 0.75rem;
		width: calc(100% - 1.25rem) !important;
	}

	&__message {
		padding: 0 1.25rem;
		width: 100%;
		color: rgb(244, 210, 120);
		font-size: 0.8125rem;
		text-shadow: 0.0875rem 0.0875rem 0.125rem rgba(0, 0, 0, 0.6);
		@include flex-col-center;
		gap: 1.25rem;
	}
}

.fight-result__detail {
	width: calc(100% - 2rem);
	@include flex-row-center;
	position: relative;
	margin: 1rem 0;
	background-image: linear-gradient(to top, rgb(37, 38, 42), rgb(66, 67, 74));

	&::before {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 0.6;
		mix-blend-mode: color-burn;
		background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
			repeat center;
		background-size: 40%;
		width: 100%;
		pointer-events: none;
		content: "";
	}

	&__content {
		@include flex-col-center;
		padding: 2rem 0.5rem;
		width: 100%;
		height: 100%;
	}

	.rewards-label {
		position: absolute;
		top: -1rem;
		z-index: 1;
		width: auto;
		height: 2rem;
	}

	:deep(.bidirectional-text-label) {
		filter: drop-shadow(0 0 0.25rem rgba(0, 0, 0, 0.6));
	}

	:deep(.price__value) {
		color: #f2dcb5 !important;
	}

	.fight-result__detail-reward {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 0.625rem;

		.price {
			min-width: 34%; // max 2 na riadok
		}
	}
	.reward-text {
		font-size: 0.875rem;
		text-align: center;
	}
}
</style>
